import { useEffect, useRef, useState } from 'react';
import Mousetrap from 'mousetrap';
import { RootState } from 'store';
import { addNotification, handleSidebarOpen } from 'store/appSlice';
import { useDispatch, useSelector } from 'react-redux';
import ModalProvider from 'context/ModalContext';
import RootModal from 'components/Modal';
import { renderRoutes, routes } from 'layouts/routes';
import { addVideo, editVideo, removeVideo } from 'store/videoSlice';
import NotificationSound from 'assets/audio/chime_bell_ding.mp3';
import _ from 'lodash';
import { addChannel, handleChannelList } from 'store/channelsSlice';
import { updateChannelPlaylist } from 'store/activitySlice';
import { addNewChat } from 'store/chatsSlice';
import { updateWorkspaceToken } from 'store/workspaceSlice';
import useActiveWorkspace from 'hooks/useActiveWorkspace';
import axios from 'axios';
import { store } from 'store';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { removeWorkspace } from 'store/workspaceSlice';
import { editNote, insertNewNote, deleteNote } from 'store/channelNoteSlice';
import { updateVideoById } from 'store/channelPlaylist';
import { insertNewPlaylist } from 'store/allPlaylists';
import { clearAllImageCache } from 'utils/imageCached';

import {
  updateTimelineSummary,
  removeTimeline,
  addTimelineSummary,
  updateTranscriptContent,
  removeTranscriptItem,
  resetTimelineUpdateStatus,
  addSummary,
  removeSummary,
  updateSummary
} from 'store/secondaryVideoTabsDataSlice';
import {
  updateVideoInPlaylists,
  removeVideoFromPlaylist,
  updatePlaylistVideoField,
  removeVideoByChannel
} from 'store/playlistSlice';

// new channel slice

import {
  addNewActivity,
  deleteActivityVideo,
  updateActivityVideoDetails,
  updateActivityVideoField,
  updateActivityVideoChannels
} from 'store/userActivitySlice';
import {
  addUserChannelVideo,
  removeUserChannelVideo,
  updateChannelVideoField,
  updateVideoTabChannels
} from 'store/userChannelVideoSlice';
import { updateSecondaryViewVideoChannels, updateSecondaryViewVideoField } from 'store/secondaryChannelVideoSlice';

import { editChat, deleteChatComment } from 'store/chatsSlice';

const parseJwt = (token: string): JwtPayload | null => {
  try {
    return jwtDecode(token) as JwtPayload;
  } catch (e) {
    return null;
  }
};

const handleErrorAndRefreshTokenstore = async () => {
  const dispatch = store.dispatch;
  const state = store.getState();
  const sessionWorkspace = sessionStorage.getItem('workspace');
  const workspaces = localStorage.getItem('workspaces') || '[]';
  const activeWorkspace = _.find(JSON.parse(workspaces), { workspace_id: sessionWorkspace });
  if (activeWorkspace && activeWorkspace.user) {
    const isValidToken = jwtDecode(activeWorkspace.user.access);
    const tokenExpiration = isValidToken ? parseJwt(activeWorkspace.user.access)?.exp : null;
    const currentTime = Math.floor(Date.now() / 1000);
    const threshold = 2 * 60 * 60; // 2 hours in seconds
    let refreshInProgress = false;

    if (tokenExpiration && tokenExpiration - currentTime <= threshold && !refreshInProgress) {
      refreshInProgress = true; // Set flag to true to indicate a refresh request is in progress
      if (!state.app.external && activeWorkspace.user.refresh) {
        const API_URL = activeWorkspace?.url;
        try {
          const rs = await axios.post(`${API_URL}/api/token/refresh/`, {
            refresh: activeWorkspace?.user.refresh
          });
          const token = rs.data;
          if (token) {
            if (activeWorkspace) {
              const payload = {
                ...token,
                ['workspaceId']: activeWorkspace.workspace_id
              };
              dispatch(updateWorkspaceToken(payload));
            }
          }
        } catch (err) {
          if (activeWorkspace) {
            const logoutUrl = activeWorkspace?.url;
            dispatch(removeWorkspace(activeWorkspace));
            window.location.replace(logoutUrl + '/logout');
          }
        } finally {
          refreshInProgress = false; // Reset flag when the request is completed (success or failure)
        }
      }
    }
  }
};

function App() {
  const dispatch = useDispatch();
  const sidebarOpen = useSelector((state: RootState) => state.app.sidebarOpen);
  const audioPlayButton = useRef<HTMLButtonElement>(null);
  const audio = new Audio(NotificationSound);
  const channelList = useSelector((state: RootState) => state.channels.channelList);
  // const allActivities = useSelector((state: RootState) => state.activity.allActivities);
  // const allVideos = useSelector((state: RootState) => state.video.allVideos);
  const ws = useRef<WebSocket | null>(null);
  const token = useSelector((state: RootState) => state.token);
  const userData = useSelector((state: RootState) => state.user.user);
  const [initWebsocketConnection, setInitWebsocketConnection] = useState(false);
  const wsChannelList = useRef<any[]>([]);
  const wsToken = useRef<any>({});
  const workspaces = useSelector((state: RootState) => state.workspace);
  const { activeWorkspace } = useActiveWorkspace();

  // Define triggerRefreshTokenIfNeeded function
  const triggerRefreshTokenIfNeeded = () => {
    const workspaceId = sessionStorage.getItem('workspace');
    if (workspaceId) {
      localStorage.setItem('last-active:workspace', workspaceId);
    }
    handleErrorAndRefreshTokenstore();
  };

  const handleVisibilityToken = () => {
    if (document.visibilityState === 'visible') {
      triggerRefreshTokenIfNeeded();
    }
  };

  useEffect(() => {
    // Add event listeners for focus, online, and offline events
    window.addEventListener('visibilitychange', handleVisibilityToken);
    window.addEventListener('online', triggerRefreshTokenIfNeeded);
    window.addEventListener('load', triggerRefreshTokenIfNeeded);

    const checkTokenInterval = setInterval(triggerRefreshTokenIfNeeded, 60 * 60 * 1000);
    // Clean up event listeners when the component unmounts
    return () => {
      window.removeEventListener('visibilitychange', triggerRefreshTokenIfNeeded);
      window.removeEventListener('online', triggerRefreshTokenIfNeeded);
      window.removeEventListener('load', triggerRefreshTokenIfNeeded);
      clearInterval(checkTokenInterval);
    };
  }, []);

  useEffect(() => {
    const handleUnload = () => {
      clearAllImageCache(); // revoke all blob URLs in memory
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  useEffect(() => {
    if (workspaces.length && activeWorkspace && channelList.length) {
      wsChannelList.current = channelList;
      const token = {
        access: activeWorkspace?.user?.access,
        refresh: activeWorkspace?.user?.refresh
      };
      wsToken.current = token;
      setInitWebsocketConnection(true);
    }
  }, [workspaces, channelList]);

  useEffect(() => {
    const token = {
      access: activeWorkspace?.user?.access,
      refresh: activeWorkspace?.user?.refresh
    };

    wsToken.current = token;
    sessionStorage.setItem('access', token.access);
  }, [activeWorkspace]);

  useEffect(() => {
    Mousetrap.bind('ctrl+shift+d', (e) => {
      e.preventDefault();
      dispatch(handleSidebarOpen(!sidebarOpen));
    });
    return () => {
      Mousetrap.unbind('ctrl+shift+d');
    };
  }, [sidebarOpen]);

  async function playAudio() {
    await audio.play();
  }
  const wsConnect = () => {
    const wssBaseUrl = activeWorkspace.ws_url;
    const websocketBaseUrl = `${wssBaseUrl}/ws`;
    const websocketUrl = `${websocketBaseUrl}/user/${activeWorkspace.user.hash_id}/?token=${wsToken.current.access}`;
    ws.current = new WebSocket(websocketUrl);
    ws.current.onopen = () => console.log('ws opened');
    ws.current.onclose = () => {
      console.log('ws closed');
      setTimeout(function () {
        console.log('Trying to reconnect in 2s');
        console.log('Reconnecting...');
        wsConnect();
      }, 2000);
    };
    ws.current.onmessage = function (event) {
      const json = JSON.parse(event.data);
      console.log('json', json);

      try {
        if (json.type === 'video_added') {
          // dispatch(addVideo({ key: 'home', data: json.video }));
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_updated') {
          dispatch(
            updateActivityVideoField({
              channelId: 'home',
              videoHashId: json.video.hash_id,
              field: 'generated_title',
              value: json.video.generated_title
            })
          );
          dispatch(
            updateChannelVideoField({
              channelId: 'home',
              videoHashId: json.video.hash_id,
              field: 'generated_title',
              value: json.video.generated_title
            })
          );
          dispatch(
            updateSecondaryViewVideoField({
              channelId: 'home',
              videoHashId: json.video.hash_id,
              field: 'generated_title',
              value: json.video.generated_title
            })
          );
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_delete') {
          dispatch(deleteActivityVideo({ channelId: 'home', videoHashId: json.video.hash_id }));
          // dispatch(removeVideo({ key: 'home', data: json.video.id }));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_created') {
          const newChannelList = [...wsChannelList.current, json.channel];
          wsChannelList.current = newChannelList;
          dispatch(addChannel(json.channel));
        } else if (json.type === 'channel_renamed') {
          console.log('channel_renamed', json.message);
        } else if (json.type === 'channel_archived') {
          console.log('channel_archived', json.message);
        } else if (json.type === 'channel_unarchived') {
          console.log('channel_unarchived', json.message);
        } else if (json.type === 'channel_deleted') {
          console.log('channel_deleted', json.message);
        } else if (json.type === 'channel_member_joined') {
          console.log('channel_member_joined', json.message);
        } else if (json.type === 'channel_member_left') {
          console.log('channel_member_left', json.message);
        } else if (json.type === 'channel_video_updated') {
          // const selectedChannel = wsChannelList.current.find((channel) => channel.id === json.channel);
          // dispatch(editVideo({ key: selectedChannel.hash_id, id: json.video.id, data: json.video }));
          // dispatch(upsertUserChannelVideo({ channelId: 'home', video: json.video }));
          // dispatch(upsertUserChannelVideo({ channelId: json.channel.hash_id, video: json.video }));

          dispatch(
            updateActivityVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'generated_title',
              value: json.video.generated_title
            })
          );
          dispatch(
            updateChannelVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'generated_title',
              value: json.video.generated_title
            })
          );
          // update playlist page, e.g: /channel/g1KA3ymY/playlist/J0dNpNaL
          dispatch(
            updatePlaylistVideoField({
              playlists: json.video.playlists,
              videoHashId: json.video.hash_id,
              field: 'generated_title',
              value: json.video.generated_title
            })
          );
          dispatch(
            updateSecondaryViewVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'generated_title',
              value: json.video.generated_title
            })
          );
          dispatch(
            updateSecondaryViewVideoField({
              channelId: 'home',
              videoHashId: json.video.hash_id,
              field: 'generated_title',
              value: json.video.generated_title
            })
          );
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_video_added') {
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'added_on_channel', action_data: { playlists: json.video.playlists } },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(
            updateActivityVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'channel_status',
              value: json.video.channel_status
            })
          );
          dispatch(
            updateActivityVideoChannels({
              channelId: 'home',
              type: 'add',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          dispatch(addNotification(json));
          // updates video tab and my-library
          dispatch(
            updateVideoTabChannels({
              channelId: json.channel.hash_id,
              type: 'add',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          dispatch(addUserChannelVideo({ channelId: json.channel.hash_id, video: json.video }));
          dispatch(
            updateSecondaryViewVideoChannels({
              channelId: json.channel.hash_id,
              type: 'add',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_video_reshared') {
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'reshared_on_channel', action_data: { playlists: json.video.playlists } },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(
            updateActivityVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'channel_status',
              value: json.video.channel_status
            })
          );
          dispatch(
            updateActivityVideoChannels({
              channelId: 'home',
              type: 'reshared',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          // update user channel videos / my library
          dispatch(
            updateVideoTabChannels({
              channelId: 'home',
              type: 'reshared',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          dispatch(
            updateChannelVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'channel_status',
              value: json.video.channel_status
            })
          );
          // update playlist page, e.g: /channel/g1KA3ymY/playlist/J0dNpNaL
          dispatch(
            updatePlaylistVideoField({
              playlists: json.video.playlists,
              videoHashId: json.video.hash_id,
              field: 'channel_status',
              value: json.video.channel_status
            })
          );
          dispatch(
            updateSecondaryViewVideoChannels({
              channelId: json.channel.hash_id,
              type: 'reshared',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_video_removed') {
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'channel_video_removed', action_data: { channels: json.video.channels } },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(
            updateActivityVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'channel_status',
              value: json.video.channel_status
            })
          );
          dispatch(
            updateActivityVideoChannels({
              channelId: 'home',
              type: 'remove',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          dispatch(addNotification(json));
          // update user channel videos / my library
          dispatch(
            updateVideoTabChannels({
              channelId: 'home',
              type: 'remove',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          // remove from user channel
          dispatch(removeUserChannelVideo({ channelId: json.channel.hash_id, videoId: json.video.id }));
          dispatch(
            updateSecondaryViewVideoChannels({
              channelId: json.channel.hash_id,
              type: 'remove',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          // remove video from playlist page, e.g: /channel/g1KA3ymY/playlist/J0dNpNaL
          // find all playlists in the current channel and remove the video from there
          dispatch(
            removeVideoByChannel({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id
            })
          );
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'event_session_synced') {
          // dispatch(addVideo({ key: 'home', data: json.video }));
          dispatch(
            addNewActivity({
              channelId: 'home',
              activity: {
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'event_session_synced', agent: json.agent, message: json.message },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_being_processed') {
          // dispatch(addVideo({ key: 'home', data: json.video }));
          dispatch(
            addNewActivity({
              channelId: 'home',
              activity: {
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'video_being_processed', agent: json.agent, message: json.message },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_has_been_processed') {
          dispatch(
            addNewActivity({
              channelId: 'home',
              activity: {
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'video_has_been_processed', agent: json.agent, message: json.message },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          // dispatch(editVideo({ key: 'home', id: json.video.id, data: json.video }));
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_video_being_processed') {
          const selectedChannel = json.channel;
          dispatch(
            addNewActivity({
              channelId: selectedChannel.hash_id,
              activity: {
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'channel_video_being_processed', agent: json.agent, message: json.message },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_video_has_been_processed') {
          const selectedChannel = json.channel;
          dispatch(
            addNewActivity({
              channelId: selectedChannel.hash_id,
              activity: {
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'channel_video_has_been_processed', agent: json.agent, message: json.message },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'member_added') {
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                content_type: 'channel_member_event',
                date_added: json.event_ts * 1000,
                details: { action: 'member_added', agent: json.agent, message: json.message },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
        } else if (json.type === 'channel_video_archived') {
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'archived_on_channel', action_data: { playlists: json.video.playlists } },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(
            updateActivityVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'channel_status',
              value: json.video.channel_status
            })
          );
          dispatch(
            updateActivityVideoChannels({
              channelId: 'home',
              type: 'archived',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          // update user channel videos / my library
          dispatch(
            updateVideoTabChannels({
              channelId: 'home',
              type: 'archived',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );
          dispatch(
            updateChannelVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'channel_status',
              value: json.video.channel_status
            })
          );
          // update playlist page, e.g: /channel/g1KA3ymY/playlist/J0dNpNaL
          dispatch(
            updatePlaylistVideoField({
              playlists: json.video.playlists,
              videoHashId: json.video.hash_id,
              field: 'channel_status',
              value: json.video.channel_status
            })
          );
          dispatch(
            updateSecondaryViewVideoChannels({
              channelId: json.channel.hash_id,
              type: 'archived',
              videoHashId: json.video.hash_id,
              channel: json.channel
            })
          );

          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_video_event') {
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'channel_video_event', action_data: { playlists: json.video.playlists } },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_member_event') {
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: {
                  action: 'channel_member_event',
                  message: json.details.message
                },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'videoshot_deleted') {
          // dispatch(editVideo({ key: 'home', id: json.video.id, data: json.video }));
          dispatch(
            addNewActivity({
              channelId: 'home',
              activity: {
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'videoshot_deleted' },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_videoshot_deleted') {
          // dispatch(editVideo({ key: 'home', id: json.video.id, data: json.video }));
          dispatch(
            addNewActivity({
              channelId: 'home',
              activity: {
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'channel_videoshot_deleted' },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_deleted') {
          // dispatch(removeVideo({ key: 'home', data: json.video.id }));
          // dispatch(updateVideoDetails({ channelId: 'home', videoHashId: json.video.hash_id, video: json.video }));
          // wsAllActivities.current['home']
          //   .filter((activity: any) => activity.content_object.id === json.video.id)
          //   .map((item: any) => {
          //     item.details.action = 'video_deleted';
          //     return item;
          //   });
          dispatch(
            addNewActivity({
              channelId: 'home',
              activity: {
                channel_hash_id: 'home',
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'video_deleted' },
                event_id: json.event_id
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'channel_video_deleted') {
          // const selectedChannel = json.channel;
          // dispatch(removeVideo({ key: selectedChannel.hash_id, data: json.video.id }));
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.video,
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: { action: 'video_deleted_from_channel' },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_note_added') {
          dispatch(
            insertNewNote({
              channelHashId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              newNote: json.note
            })
          );

          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.video,
                content_type: 'note',
                date_added: json.event_ts * 1000,
                details: {
                  action: 'added_video_note',
                  action_data: { note: json.note }
                },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_note_updated') {
          dispatch(
            editNote({
              channelHashId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              shotHashId: json.note.shot_hash_id,
              noteHashId: json.note.hash_id,
              updatedContent: json.note
            })
          );
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.video,
                content_type: 'note',
                date_added: json.event_ts * 1000,
                details: {
                  action: 'video_note_updated',
                  action_data: { note: json.note }
                },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_note_deleted') {
          dispatch(
            deleteNote({
              channelHashId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              shotHashId: json.note.shot_hash_id,
              noteHashId: json.note.hash_id
            })
          );
        } else if (json.type === 'video_comment_added') {
          dispatch(
            addNewChat({
              comment: json.comment,
              hash_id: json.video_id, // videoHashId
              showNewlyAdded: userData.id !== json.user.id
            })
          );
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: json.comment,
                content_type: 'comment',
                date_added: json.event_ts * 1000,
                details: {
                  action: 'video_comment_added',
                  action_data: { comment: json.comment }
                },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'video_comment_updated') {
          console.log('video_comment_updated', json);
          dispatch(
            editChat({ videoHashId: json.video_id, hashId: json.comment.hash_id, updatedContent: json.comment.content })
          );
        } else if (json.type === 'video_comment_deleted') {
          console.log('video_comment_deleted', json.comm);
          dispatch(deleteChatComment({ hash_id: json.video_id, commentId: json.comment.hash_id }));
        } else if (json.type === 'playlist_added') {
          const playlist = json.playlist;
          dispatch(insertNewPlaylist({ channelHashId: playlist.channel_hash_id, newPlaylist: playlist }));
        } else if (json.type === 'playlist_updated') {
          console.log('playlist_updated', json.message);
        } else if (json.type === 'playlist_deleted') {
          console.log('playlist_deleted', json.message);
        } else if (json.type === 'playlist_video_update') {
          // dispatch(
          //   updateChannelPlaylist({ key: json.channel.hash_id, id: json.video.id, playlists: json.video.playlists })
          // );
          dispatch(
            addNewActivity({
              channelId: json.channel.hash_id,
              activity: {
                channel_hash_id: json.channel.hash_id,
                content_object: {
                  ...json.video,
                  playlists: json.playlists
                },
                content_type: 'video info',
                date_added: json.event_ts * 1000,
                details: {
                  action: 'playlist_updated',
                  action_data: {
                    playlists: json.video.playlists,
                    added_playlist: json.added_playlist,
                    removed_playlist: json.removed_playlist
                  }
                },
                event_id: json.event_id,
                user: json.user
              }
            })
          );
          dispatch(
            updateActivityVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'playlists',
              value: json.playlists
            })
          );
          // dispatch(editVideo({ key: json.channel.hash_id, id: json.video.id, data: updatedPlaylistVideo }));
          dispatch(updateVideoById({ videoId: json.video.id, updatedVideo: json.video }));
          if (json.removed_playlist?.length) {
            dispatch(removeVideoFromPlaylist({ playlists: json.removed_playlist, videoHashId: json.video.hash_id }));
          }
          dispatch(
            updateVideoInPlaylists({
              videoId: json.video.id,
              updatedVideo: json.video,
              updatedPlaylists: json.playlists
            })
          );
          dispatch(
            updateChannelVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'playlists',
              value: json.playlists
            })
          );
          dispatch(
            updateSecondaryViewVideoField({
              channelId: json.channel.hash_id,
              videoHashId: json.video.hash_id,
              field: 'playlists',
              value: json.playlists
            })
          );
          dispatch(addNotification(json));
          if (audioPlayButton.current) {
            audioPlayButton.current.click();
          }
        } else if (json.type === 'sentence_updated') {
          const payload = {
            sentenceHashId: json.sentence.sentence_hash_id,
            sentence: json.sentence,
            videoShotId: json.shot_id
          };
          dispatch(updateSummary(payload));
        } else if (json.type === 'sentence_deleted') {
          dispatch(removeSummary({ videoShotId: json.shot_id, sentenceHashId: json.sentence.sentence_hash_id }));
        } else if (json.type === 'sentence_added') {
          dispatch(addSummary({ videoShotId: json.shot_id, summary: json.sentence }));
        } else if (json.type === 'sentence_speaker_changed') {
          dispatch(
            updateSummary({
              sentenceHashId: json.sentence.sentence_hash_id,
              sentence: json.sentence,
              videoShotId: json.shot_id
            })
          );
        } else if (json.type === 'timeline_updated') {
          dispatch(
            updateTimelineSummary({
              videoShotId: json.shot_id,
              timelinehashId: json.timeline.hash_id,
              timeline: json.timeline
            })
          );
          dispatch(resetTimelineUpdateStatus({ videoShotId: json.shot_id, status: true }));
        } else if (json.type === 'timeline_speaker_changed') {
          dispatch(
            updateTimelineSummary({
              videoShotId: json.shot_id,
              timelinehashId: json.timeline.hash_id,
              timeline: json.timeline
            })
          );
          dispatch(resetTimelineUpdateStatus({ videoShotId: json.shot_id, status: true }));
        } else if (json.type === 'timeline_deleted') {
          dispatch(
            removeTimeline({
              videoShotId: json.shot_id,
              timelinehashId: json.timeline.hash_id
            })
          );
        } else if (json.type === 'timeline_added') {
          dispatch(
            addTimelineSummary({
              videoShotId: json.shot_id,
              timeline: json.timeline
            })
          );
          dispatch(resetTimelineUpdateStatus({ videoShotId: json.shot_id, status: true }));
        } else if (json.type === 'video_transcript_updated') {
          dispatch(
            updateTranscriptContent({
              videoShotId: json.transcript.shot_hash_id,
              transcriptHashId: json.transcript.hash_id,
              transcript: json.transcript
            })
          );
        } else if (json.type === 'video_transcript_deleted') {
          dispatch(
            removeTranscriptItem({
              videoShotId: json.transcript.shot_hash_id,
              transcriptHashId: json.transcript.hash_id
            })
          );
        }
      } catch (err) {
        console.log(err);
      }
    };
  };

  useEffect(() => {
    if (initWebsocketConnection) {
      wsConnect();
    }
    const wsCurrent = ws.current;
    return () => {
      wsCurrent?.close();
    };
  }, [initWebsocketConnection]);

  return (
    <ModalProvider>
      <div id="parent" className={`flex flex-col relative min-h-screen max-h-screen`}>
        <button ref={audioPlayButton} className="hidden" onClick={playAudio}>
          Play
        </button>
        {renderRoutes(routes)}
        <RootModal />
      </div>
    </ModalProvider>
  );
}

export default App;
